import { useCallback, useContext, useState } from 'react';
import { InfluencerDataContext } from '../../../contexts/InfluencerDataContext';
import { walletApi } from '../../../services/api/wallet';
import { useWhopPayoutsModal } from '../contexts/WhopPayoutsModalContext';
import { openKycPanelAfterSetup } from '../utils/walletSetupPayoutsUtils';

export interface UseSetupPayoutsOptions {
  influencerId: string | undefined;
  setKycApproved: (approved: boolean) => void;
  onCompanyId: (companyId: string) => void;
  onWithdrawalComplete?: () => void;
}

export function useSetupPayouts({
  influencerId,
  setKycApproved,
  onCompanyId,
  onWithdrawalComplete,
}: UseSetupPayoutsOptions) {
  const { isSwitchedAccount } = useContext(InfluencerDataContext);
  const { openPayoutsPanel } = useWhopPayoutsModal();
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);

  const setupPayouts = useCallback(async () => {
    if (!influencerId || isSwitchedAccount) {
      return;
    }

    setConnecting(true);
    setConnectError(null);
    try {
      const result = await walletApi.setupAccount({ influencerId });
      onCompanyId(result.companyId);
      setKycApproved(result.kycApproved);

      try {
        await openKycPanelAfterSetup({
          influencerId,
          companyId: result.companyId,
          kycApproved: result.kycApproved,
          setKycApproved,
          onWithdrawalComplete: onWithdrawalComplete ?? (() => {}),
          openPayoutsPanel,
        });
      } catch {
        // Account is created; user can open Verify yourself once bootstrap catches up.
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not start payout setup';
      setConnectError(message);
    } finally {
      setConnecting(false);
    }
  }, [
    influencerId,
    isSwitchedAccount,
    onCompanyId,
    onWithdrawalComplete,
    openPayoutsPanel,
    setKycApproved,
  ]);

  return { setupPayouts, connecting, connectError, setConnectError };
}
